/**
 * App shell combining gradient background, glass sidebar, header, and main content.
 * Uses TanStack Router <Outlet /> for child route rendering.
 */

import { Outlet, useRouterState } from "@tanstack/react-router";
import { Sidebar } from "./sidebar";
import { Header } from "./header";
import { GradientBackground } from "./gradient-background";
import { HelpDrawer } from "./help-drawer";
import { ErrorBoundary } from "@/components/sc/error-boundary";
import { useHelp } from "@/hooks/use-help";

const ROUTE_META: Record<string, { title: string; subtitle?: string }> = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Overview of your GDAP environment",
  },
  "/customers": { title: "Customers", subtitle: "Managed customer tenants" },
  "/technicians": {
    title: "Technicians",
    subtitle: "Team access management",
  },
  "/pim-sessions": {
    title: "PIM Sessions",
    subtitle: "Privileged identity sessions",
  },
  "/gdap": {
    title: "GDAP Relationships",
    subtitle: "Delegated admin privilege management",
  },
  "/audit-log": {
    title: "Audit Log",
    subtitle: "Security and access events",
  },
  "/scheduled-tasks": {
    title: "Scheduled Tasks",
    subtitle: "Background job monitoring and management",
  },
  "/settings": { title: "Settings", subtitle: "Application configuration" },
  "/approval-policies": {
    title: "Approval Policies",
    subtitle: "Per-tier approval requirements for PIM activation",
  },
  "/pim-approvals": {
    title: "PIM Approvals",
    subtitle: "Pending activation approval requests",
  },
};

export function AppShell() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const help = useHelp();

  // Match on the base path (strip detail route params)
  const basePath =
    Object.keys(ROUTE_META).find((path) => currentPath.startsWith(path)) ??
    currentPath;
  const { title, subtitle } = ROUTE_META[basePath] ?? { title: "Page" };

  return (
    <div className="flex min-h-screen">
      <GradientBackground />
      <Sidebar />
      <div className="flex flex-1 flex-col">
        <Header title={title} subtitle={subtitle} onHelpToggle={help.toggle} />
        <main className="flex-1 p-8">
          <ErrorBoundary key={basePath}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <HelpDrawer isOpen={help.isOpen} onClose={help.close} content={help.content} />
    </div>
  );
}
